import styles from '@/styles/more-time.module.css';

import { useEffect, useRef } from 'react';
import { IconX } from '@tabler/icons-react';

interface FilmLightboxProps {
  videoId: string | null;
  title: string;
  onClose: () => void;
}

/**
 * Full-screen film player. The privacy-enhanced YouTube player only loads once a visitor presses play.
 * Escape or the close button dismisses it; focus moves into the dialog and returns to the opener.
 * Keys pressed inside the cross-origin player never reach this window, so the focus trap uses guard
 * elements on either side of it: tabbing out of the player in either direction lands back on the close
 * button, where Escape works again.
 */
const FilmLightbox = ({ videoId, title, onClose }: FilmLightboxProps) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!videoId) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, [videoId, onClose]);

  if (!videoId) return null;

  return (
    <div
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <span tabIndex={0} data-focus-guard="start" onFocus={() => frameRef.current?.focus()} />
      <button ref={closeRef} type="button" className={styles.lightboxClose} onClick={onClose}>
        <IconX size={22} aria-hidden="true" />
        <span className="sr-only">Close film</span>
      </button>
      <div className={styles.lightboxFrame}>
        <iframe
          ref={frameRef}
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <span tabIndex={0} data-focus-guard="end" onFocus={() => closeRef.current?.focus()} />
    </div>
  );
};

export default FilmLightbox;
